import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckIcon from '@mui/icons-material/Check';
import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import createOrUpdateQuote from '../../../../../../hooks/quotes/createOrUpdateQuote.jsx';
import { setSnackBar } from '../../../../../../states/app/staticSlice.js';
import { QuotesFormHeader } from './QuotesFormHeader.jsx';
import StepOne from './StepOne.jsx';
import StepThree from './StepThree.jsx';
import StepTwo from './StepTwo.jsx';

const steps = ['Informations client', 'Détails commande', 'Récapitulatif'];

const QuoteForm = ({
  ticket,
  handleShowComponent,
  draftQuote,
  profile,
  mutateTicket,
}) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    isSet: false,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    billingAddress: {
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
    },
    status: 'DRAFT',
    contact: '',
    project: '',
    ticket: '',
    author: '',
    store: '',
    currency: '',
    amounts: {
      priceHt: 0,
      priceTtc: 0,
      priceHtNet: 0,
      priceTtcNet: 0,
      totalVat: 0,
      tvaValue: 20,
      discount: 0,
      discountAmount: 0,
    },
    lines: [],
  });
  const [formError, setFormError] = useState({
    contact: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
    },
    billingAddress: {
      address1: '',
      address2: '',
      city: '',
      state: '',
      zip: '',
    },
  });

  // Fonction pour transformer postalCode en zipCode avant l'envoi API
  const transformDataForAPI = (data, isConfirm = false) => {
    const transformedData = { ...data };
    if (transformedData.billingAddress?.postalCode) {
      transformedData.billingAddress = {
        ...transformedData.billingAddress,
        zipCode: transformedData.billingAddress.postalCode,
      };
      delete transformedData.billingAddress.postalCode;
    }
    if (isConfirm) {
      transformedData.status = 'VALIDATED';
    }
    return transformedData;
  };

  const handleNext = async () => {
    try {
      switch (activeStep) {
        case 0:
        case 1:
          await handleSave({ isFromStepChange: true });
          setActiveStep((prevStep) => prevStep + 1);
          break;
        case 2:
          await handleConfirm();
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error creating or updating quotes:', error);
      dispatch(
        setSnackBar({
          open: true,
          message: "Une erreur est survenue lors de l'opération",
          color: 'error',
        })
      );
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleFormChange = useCallback(({ key, value }) => {
    setFormData((prevData) => {
      // Gestion des clés imbriquées (ex: "shippingAddress.address1")
      if (key.includes('.')) {
        const [parent, child] = key.split('.');
        return {
          ...prevData,
          [parent]: {
            ...prevData[parent],
            [child]: value,
          },
        };
      }
      // Gestion des clés simples
      return {
        ...prevData,
        [key]: value,
      };
    });
  }, []);

  const handleSave = async ({ isFromStepChange = false }) => {
    try {
      if (isFromStepChange) {
        setIsLoading(true);
      } else {
        setIsSaveLoading(true);
      }
      const transformedData = transformDataForAPI(formData);
      const response = await createOrUpdateQuote(ticket._id, transformedData);
      mutateTicket();
      dispatch(
        setSnackBar({
          open: true,
          message: 'Brouillon de devis enregistré avec succès',
          color: 'success',
        })
      );
      console.log('response', response);
    } catch (error) {
      console.error('Error creating or updating quotes:', error);
      dispatch(
        setSnackBar({
          open: true,
          message: "Erreur lors de l'enregistrement du devis",
          color: 'error',
        })
      );
    } finally {
      if (isFromStepChange) {
        setIsLoading(false);
      } else {
        setIsSaveLoading(false);
      }
    }
  };

  const handleConfirm = async () => {
    try {
      setIsConfirmLoading(true);
      const transformedData = transformDataForAPI(formData, true);
      const response = await createOrUpdateQuote(ticket._id, transformedData);
      console.log('response', response);
      mutateTicket();
      dispatch(
        setSnackBar({
          open: true,
          message: 'Devis créé avec succès',
          color: 'success',
        })
      );
      handleShowComponent(null);
    } catch (error) {
      console.error('Error creating or updating quotes:', error);
      dispatch(
        setSnackBar({
          open: true,
          message: 'Erreur lors de la création du devis',
          color: 'error',
        })
      );
    } finally {
      setIsConfirmLoading(false);
    }
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <StepOne
            formData={formData}
            handleFormChange={handleFormChange}
            formError={formError}
            ticket={ticket}
          />
        );
      case 1:
        return (
          <StepTwo
            ticket={ticket}
            formData={formData}
            handleFormChange={handleFormChange}
            formError={formError}
            setFormError={setFormError}
            handleSave={handleSave}
          />
        );
      case 2:
        return <StepThree formData={formData} />;
      default:
        return 'Étape inconnue';
    }
  };

  const getEndIcon = () => {
    if (isLoading) {
      return <CircularProgress size={20} />;
    }
    if (activeStep === steps.length - 1) {
      return <CheckIcon />;
    }
    return <ArrowForwardIcon />;
  };

  useEffect(() => {
    if (ticket && formData.isSet === false) {
      const ticketBillingAddress =
        ticket?.contact?.addresses?.find((address) => address.isBilling) || {};

      console.log('ticketBillingAddress', ticketBillingAddress);
      console.log('draftQuote', draftQuote);

      if (draftQuote) {
        // Mapper les données du draftQuote vers formData
        setFormData({
          ...formData,
          isSet: true,
          email: draftQuote?.contact.email,
          firstName: draftQuote?.contact.firstName,
          lastName: draftQuote?.contact.lastName,
          phone:
            draftQuote?.contact.internationalPhone ||
            draftQuote?.billingAddress?.phone,
          billingAddress: {
            address1: draftQuote?.billingAddress?.line1 || '',
            address2: draftQuote?.billingAddress?.line2 || '',
            city: draftQuote?.billingAddress?.city || '',
            postalCode: draftQuote?.billingAddress?.zipCode || '',
            country: draftQuote?.billingAddress?.country?.code2 || '',
          },
          contact: draftQuote?.contact._id,
          project: draftQuote?.project._id,
          ticket: draftQuote?.ticket._id,
          author: draftQuote?.author._id,
          store: draftQuote?.store.shortName,
          currency: draftQuote?.currency?.code || '',
          discount: draftQuote?.discount || 0,
          lines: draftQuote?.lines || [],
          status: draftQuote?.status?.code || 'DRAFT',
        });
      } else {
        setFormData({
          ...formData,
          isSet: true,
          email: ticket.contact.email,
          firstName: ticket.contact.firstName,
          lastName: ticket.contact.lastName,
          phone:
            ticket.contact.phone ||
            ticket.contact.addresses.find((address) => address.isBilling)
              ?.phone,
          billingAddress: {
            address1: ticketBillingAddress.line1,
            address2: ticketBillingAddress.line2,
            city: ticketBillingAddress.city,
            postalCode: ticketBillingAddress?.zipCode,
            country: ticketBillingAddress?.country?.code2,
          },
          contact: ticket.contact._id,
          project: ticket.project._id,
          ticket: ticket._id,
          author: profile._id,
          store: ticket.contact.store.shortName,
          currency: ticket.contact.store?.country?.currency?.code,
          discount: 0,
          lines: [],
          status: 'DRAFT',
        });
      }
    }
  }, [ticket, draftQuote, profile]);

  return (
    <Box sx={{ width: '100%' }}>
      <QuotesFormHeader
        handleShowComponent={() => handleShowComponent(null)}
        draftQuote={draftQuote}
      />
      <Paper elevation={3} sx={{ p: 3 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === steps.length ? (
          <>
            <Typography sx={{ mt: 2, mb: 1 }}>
              Toutes les étapes sont terminées
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
              <Box sx={{ flex: '1 1 auto' }} />
              <Button onClick={() => handleShowComponent(null)}>Retour</Button>
            </Box>
          </>
        ) : (
          <>
            <Typography sx={{ mt: 2, mb: 1 }}>
              {getStepContent(activeStep)}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
              {activeStep !== 0 && (
                <Button
                  color="primary"
                  variant="outlined"
                  onClick={handleBack}
                  sx={{ mr: 1 }}
                >
                  Retour
                </Button>
              )}
              <Box sx={{ flex: '1 1 auto' }} />
              <Box>
                {activeStep !== 0 && (
                  <Button
                    color="primary"
                    variant="outlined"
                    onClick={handleSave}
                    disabled={isSaveLoading}
                    endIcon={
                      isSaveLoading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <SaveIcon />
                      )
                    }
                  >
                    Enregistrer
                  </Button>
                )}
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleNext}
                  disabled={isLoading || isConfirmLoading}
                  sx={{ ml: 1 }}
                  endIcon={getEndIcon()}
                >
                  {activeStep === steps.length - 1 ? 'Confirmer' : 'Suivant'}
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default QuoteForm;
